import { useCallback, useEffect, useMemo, useState } from "react";
import {
  extractSlashQuery,
  filterMarcusSlashCommands,
  type MarcusSlashCommand,
} from "../lib/marcusSlashCommands";

interface UseSlashCommandPaletteOptions {
  inputValue: string;
  commands: readonly MarcusSlashCommand[] | undefined;
  onSelect: ((cmd: MarcusSlashCommand) => void) | undefined;
}

export interface SlashCommandPaletteController {
  /** Filtered list to render. Empty when palette is closed or query has no matches. */
  filteredCommands: readonly MarcusSlashCommand[];
  /** Whether the popover should be visible. */
  isOpen: boolean;
  /** Whether to render category section headers (only when query is bare `/`). */
  showCategories: boolean;
  /** Clamped index into `filteredCommands` for the highlighted row. */
  activeIndex: number;
  /** Sync activeIndex on hover. */
  setActiveIndex: (index: number) => void;
  /** Fire when a row is clicked. */
  selectCommand: (cmd: MarcusSlashCommand) => void;
  /**
   * Pass-through key handler. Returns `true` when the palette consumed the
   * key (ArrowUp/Down nav, Enter/Tab to select with matches, Escape to
   * dismiss). The caller should fall through to the chat's default
   * handler when this returns `false`.
   */
  handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
}

/**
 * Owns all state for the Marcus slash-command palette so `CompanionInput`
 * stays focused on rendering. Stateless when `commands` is undefined.
 */
export function useSlashCommandPalette({
  inputValue,
  commands,
  onSelect,
}: UseSlashCommandPaletteOptions): SlashCommandPaletteController {
  const hasCommands = !!commands && commands.length > 0;

  const slashQuery = useMemo(
    () => (hasCommands ? extractSlashQuery(inputValue) : null),
    [hasCommands, inputValue],
  );
  const filteredCommands = useMemo(
    () =>
      hasCommands && slashQuery !== null && commands
        ? filterMarcusSlashCommands(slashQuery, commands)
        : [],
    [commands, hasCommands, slashQuery],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [paletteDismissed, setPaletteDismissed] = useState(false);

  // Reset the Escape-dismissal once the user backs out of slash mode so the
  // palette can reopen on the next `/`.
  useEffect(() => {
    if (slashQuery === null && paletteDismissed) setPaletteDismissed(false);
  }, [slashQuery, paletteDismissed]);

  // Snap activeIndex back to 0 whenever the visible options change.
  useEffect(() => {
    setActiveIndex(0);
  }, [filteredCommands.length, slashQuery]);

  const isOpen = hasCommands && slashQuery !== null && !paletteDismissed;
  const showCategories = isOpen && slashQuery === "/";
  const safeActiveIndex =
    filteredCommands.length === 0
      ? 0
      : Math.min(Math.max(activeIndex, 0), filteredCommands.length - 1);

  const selectCommand = useCallback(
    (cmd: MarcusSlashCommand) => {
      setPaletteDismissed(false);
      setActiveIndex(0);
      onSelect?.(cmd);
    },
    [onSelect],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!isOpen) return false;
      // Shift+Enter is always "insert newline" — leave it to the parent.
      if (event.key === "Enter" && event.shiftKey) return false;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (filteredCommands.length > 0) {
          setActiveIndex((idx) => (idx + 1) % filteredCommands.length);
        }
        return true;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (filteredCommands.length > 0) {
          setActiveIndex(
            (idx) => (idx - 1 + filteredCommands.length) % filteredCommands.length,
          );
        }
        return true;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setPaletteDismissed(true);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        if (filteredCommands.length === 0) return false;
        event.preventDefault();
        selectCommand(filteredCommands[safeActiveIndex]);
        return true;
      }
      return false;
    },
    [filteredCommands, isOpen, safeActiveIndex, selectCommand],
  );

  return {
    filteredCommands,
    isOpen,
    showCategories,
    activeIndex: safeActiveIndex,
    setActiveIndex,
    selectCommand,
    handleKeyDown,
  };
}
